/**
 * Auto Free Product Manager
 * Automatically adds a free product to cart when threshold is met
 *
 * Configuration: Set via theme settings (window.themeVariables.autoFreeProductConfig)
 */

(function() {
  'use strict';

  // Configuration - Will be overridden by theme settings if available
  let CONFIG = {
    enabled: false,
    variantId: null,
    threshold: 5000, // in cents (50.00 GBP)
    includeSubscriptions: false,
    currency: 'GBP'
  };

  // Load config from theme settings if available
  if (window.themeVariables?.autoFreeProductConfig) {
    CONFIG = window.themeVariables.autoFreeProductConfig;
  }

  // State management
  let isProcessing = false;
  let isFetchingCart = false;
  let pendingCartUpdate = false;
  let debounceTimer = null;
  let lastCartState = null;
  let initialCartLoaded = false;

  /**
   * Cleanup any leftover auto gifts when feature is disabled
   */
  async function cleanupAutoGifts() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      const gifts = (cart.items || []).filter(item => item.properties && item.properties._free_gift === 'auto');

      if (!gifts.length) return;

      for (const gift of gifts) {
        await removeFreeProduct(gift.key);
      }

      await refreshCartUI();
    } catch (error) {
      console.error('Auto-free-product: Error cleaning up gifts while disabled', error);
    }
  }

  /**
   * Validate configuration
   */
  function validateConfig() {
    if (!CONFIG.enabled) {
      console.log('Auto-free-product: Feature disabled');
      return false;
    }

    if (!CONFIG.variantId || CONFIG.variantId === '') {
      console.warn('Auto-free-product: No variant ID configured');
      return false;
    }

    const variantIdNum = parseInt(CONFIG.variantId);
    if (isNaN(variantIdNum) || variantIdNum <= 0) {
      console.warn('Auto-free-product: Invalid variant ID');
      return false;
    }

    if (!CONFIG.threshold || CONFIG.threshold <= 0) {
      console.warn('Auto-free-product: Invalid threshold');
      return false;
    }

    return true;
  }

  /**
   * Calculate eligible cart total
   */
  function calculateEligibleTotal(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }

    return cart.items.reduce((total, item) => {
      // Skip free gifts
      if (item.properties &&
          item.properties._free_gift === 'auto' &&
          item.variant_id === parseInt(CONFIG.variantId)) {
        return total;
      }

      // Skip subscriptions if configured
      if (!CONFIG.includeSubscriptions && item.selling_plan_allocation) {
        return total;
      }

      return total + item.final_line_price;
    }, 0);
  }

  /**
   * Find existing free gift in cart
   */
  function findExistingFreeGift(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return null;
    }

    return cart.items.find(item =>
      item.properties &&
      item.properties._free_gift === 'auto' &&
      item.variant_id === parseInt(CONFIG.variantId)
    ) || null;
  }

  /**
   * Add free product to cart
   */
  async function addFreeProduct() {
    console.log('Auto-free-product: Adding free product', CONFIG.variantId);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(CONFIG.variantId),
            quantity: 1,
            properties: {
              '_free_gift': 'auto',
              '_threshold': CONFIG.threshold.toString(),
              '_auto_added': new Date().toISOString()
            }
          }]
        })
      });

      if (!response.ok) {
        if (response.status === 422) {
          console.warn('Auto-free-product: Product unavailable');
          return false;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      console.log('Auto-free-product: Free product added');
      return true;
    } catch (error) {
      console.error('Auto-free-product: Error adding product', error);
      return false;
    }
  }

  /**
   * Remove free product from cart
   */
  async function removeFreeProduct(giftKey) {
    console.log('Auto-free-product: Removing free product');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: giftKey,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      console.log('Auto-free-product: Free product removed');
      return true;
    } catch (error) {
      console.error('Auto-free-product: Error removing product', error);
      return false;
    }
  }

  /**
   * Normalize gift quantity to 1 if it exists with higher quantity
   */
  async function normalizeGiftQuantity(existingGift) {
    if (!existingGift || existingGift.quantity <= 1) {
      return false;
    }

    console.log('Auto-free-product: Normalizing free product quantity to 1');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: existingGift.key,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      console.log('Auto-free-product: Free product quantity normalized');
      return true;
    } catch (error) {
      console.error('Auto-free-product: Error normalizing product quantity', error);
      return false;
    }
  }

  /**
   * Refresh cart UI (optimized single-fetch approach)
   */
  async function refreshCartUI() {
    console.log('Auto-free-product: Refreshing cart UI');

    try {
      // On main cart page, refresh section markup instead of full reload
      if (window.location.pathname === '/cart') {
        console.log('Auto-free-product: Main cart page - refreshing section');

        const cartUrl = window.themeVariables?.routes?.cartUrl || '/cart';

        const [cartResponse, sectionResponse] = await Promise.all([
          fetch('/cart.js'),
          fetch(`${cartUrl}?section_id=main`)
        ]);

        if (!cartResponse.ok || !sectionResponse.ok) {
          throw new Error(`Cart refresh failed: ${cartResponse.status}/${sectionResponse.status}`);
        }

        const cart = await cartResponse.json();
        const sectionHtml = await sectionResponse.text();

        const cartSection = document.querySelector('#shopify-section-main');

        if (cartSection) {
          cartSection.innerHTML = sectionHtml;
        } else {
          console.warn('Auto-free-product: Cart section not found in DOM, falling back to reload');
          window.location.reload();
          return;
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              cart: cart
            }
          })
        );

        console.log('Auto-free-product: Cart UI refreshed (section render)');
        return;
      }

      // For mini cart, fetch cart with sections in ONE request
      // The /cart/update.js response contains BOTH cart data AND sections HTML
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sections: ['mini-cart']
        })
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const cart = await response.json();

      // Dispatch cart:refresh event with cart data (includes sections)
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cart
          }
        })
      );

      console.log('Auto-free-product: Cart UI refreshed');
    } catch (error) {
      console.error('Auto-free-product: Error refreshing cart UI', error);
      // Final fallback to reload to avoid stale UI
      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
    }
  }

  /**
   * Main evaluation logic (matches cart-gifts pattern)
   */
  async function evaluateCart(cart) {
    if (!CONFIG.enabled || isProcessing) {
      // Mark pending if we skipped while enabled
      if (CONFIG.enabled && isProcessing) {
        pendingCartUpdate = true;
      }
      return;
    }

    // Validate cart data
    if (!cart || !Array.isArray(cart.items)) {
      console.warn('Auto-free-product: Invalid cart data');
      return;
    }

    // Create cart signature to detect if it actually changed
    const cartSignature = JSON.stringify({
      itemCount: cart.item_count,
      totalPrice: cart.total_price,
      items: cart.items.map(item => ({
        key: item.key,
        variant_id: item.variant_id,
        quantity: item.quantity
      }))
    });

    // Skip if cart hasn't changed
    if (lastCartState === cartSignature) {
      console.log('Auto-free-product: Cart unchanged, skipping evaluation');
      return;
    }

    lastCartState = cartSignature;
    isProcessing = true;

    try {
      const eligibleTotal = calculateEligibleTotal(cart);
      const thresholdMet = eligibleTotal >= CONFIG.threshold;
      const existingFreeGift = findExistingFreeGift(cart);

      console.log('Auto-free-product: Evaluation', {
        eligible: eligibleTotal,
        threshold: CONFIG.threshold,
        thresholdMet,
        hasGift: !!existingFreeGift
      });

      let changesMade = false;

      // Determine what action to take
      if (thresholdMet && !existingFreeGift) {
        // Threshold met, no gift - add it
        console.log('Auto-free-product: Adding free product');
        const added = await addFreeProduct();
        changesMade = added;
      } else if (!thresholdMet && existingFreeGift) {
        // Threshold not met, gift exists - remove it
        console.log('Auto-free-product: Removing free product');
        const removed = await removeFreeProduct(existingFreeGift.key);
        changesMade = removed;
      } else if (thresholdMet && existingFreeGift && existingFreeGift.quantity > 1) {
        // Threshold met, but gift quantity should be exactly 1
        console.log('Auto-free-product: Normalizing excess gift quantity');
        const normalized = await normalizeGiftQuantity(existingFreeGift);
        changesMade = normalized;
      } else {
        console.log('Auto-free-product: No action needed');
      }

      // Only refresh UI once at the end if changes were made
      if (changesMade) {
        await refreshCartUI();
        // Clear cart state so next evaluation runs
        lastCartState = null;
      }
    } catch (error) {
      console.error('Auto-free-product: Error evaluating cart', error);
    } finally {
      isProcessing = false;
      // If an update was skipped while processing, re-run once
      if (pendingCartUpdate) {
        pendingCartUpdate = false;
        handleCartUpdate();
      }
    }
  }

  /**
   * Handle cart update events with debouncing (prevents race conditions)
   */
  async function handleCartUpdate(event) {
    initialCartLoaded = true;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      // Check if already processing before fetching
      if (isProcessing || isFetchingCart) {
        console.log('Auto-free-product: Already processing, skipping');
        pendingCartUpdate = true;
        return;
      }

      console.log('Auto-free-product: Cart update detected');

      try {
        isFetchingCart = true;
        let cart;

        // Use cart data from event if available (avoids race condition)
        if (event?.detail?.cart) {
          console.log('Auto-free-product: Using cart data from event');
          cart = event.detail.cart;
        } else {
          // Fetch fresh cart state only if not provided in event
          console.log('Auto-free-product: Fetching fresh cart data');
          const response = await fetch('/cart.js');
          cart = await response.json();
        }

        await evaluateCart(cart);
      } catch (error) {
        console.error('Auto-free-product: Error fetching cart', error);
      } finally {
        isFetchingCart = false;
      }
    }, 300); // 300ms debounce - matches cart-gifts for reliability
  }

  /**
   * Initialize
   */
  function init() {
    if (!validateConfig()) {
      console.log('Auto-free-product: Not initialized (invalid config)');
      cleanupAutoGifts();
      return;
    }

    console.log('Auto-free-product: Initializing', CONFIG);

    // Avoid duplicate listeners
    if (window.autoFreeProductManager?._initialized) {
      console.log('Auto-free-product: Already initialized, skipping');
      return;
    }

    // Listen to cart events (these contain fresh cart data in event.detail.cart)
    // Note: We don't listen to variant:added/variant:changed because they fire
    // before the cart data is updated, causing race conditions. Instead, we rely
    // on cart:updated which fires after the cart fetch completes.
    document.documentElement.addEventListener('cart:updated', handleCartUpdate);
    document.documentElement.addEventListener('cart:refresh', handleCartUpdate);

    // Listen for page visibility changes (handles back button, tab switching)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        console.log('Auto-free-product: Page became visible, re-evaluating');
        handleCartUpdate();
      }
    });

    // Initial evaluation on page load (with delay to ensure DOM is ready)
    setTimeout(async () => {
      try {
        if (initialCartLoaded || isFetchingCart) return;
        isFetchingCart = true;
        const response = await fetch('/cart.js');
        const cart = await response.json();
        await evaluateCart(cart);
      } catch (error) {
        console.error('Auto-free-product: Error during initial cart fetch', error);
      } finally {
        isFetchingCart = false;
      }
    }, 500);

    console.log('Auto-free-product: Event listeners attached');

    window.autoFreeProductManager = {
      ...(window.autoFreeProductManager || {}),
      _initialized: true,
      config: CONFIG,
      checkCart: async () => {
        if (isFetchingCart) return;
        isFetchingCart = true;
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          await evaluateCart(cart);
        } finally {
          isFetchingCart = false;
        }
      }
    };
  }

  // Initialize immediately
  init();

})();
